/**
 * Analytics Service
 *
 * Gère le tracking des événements utilisateur via funnel_id et session_id.
 * Le funnel_id représente un parcours complet (recherche → course créée).
 * Le session_id représente une session utilisateur (peut contenir plusieurs funnels).
 */
class AnalyticsService {
  constructor() {
    this.FUNNEL_ID_KEY = 'rj_funnel_id';
    this.SESSION_ID_KEY = 'rj_session_id';
  }

  /**
   * Récupère ou génère un funnel_id
   * Un funnel_id représente un parcours complet (recherche → course créée)
   * @returns {string} Le funnel_id actuel ou nouvellement généré
   */
  getFunnelId() {
    // Vérifier si on est côté client
    if (typeof window === 'undefined' || !window.localStorage) {
      return this.generateUUID();
    }

    let funnelId = localStorage.getItem(this.FUNNEL_ID_KEY);

    if (!funnelId) {
      funnelId = this.generateUUID();
      localStorage.setItem(this.FUNNEL_ID_KEY, funnelId);
      console.log('[Analytics] New funnel started:', funnelId);
    }

    return funnelId;
  }

  /**
   * Récupère ou génère un session_id
   * Un session_id représente une session utilisateur (peut contenir plusieurs funnels)
   * @returns {string} Le session_id actuel ou nouvellement généré
   */
  getSessionId() {
    // Vérifier si on est côté client
    if (typeof window === 'undefined' || !window.localStorage) {
      return this.generateUUID();
    }

    let sessionId = localStorage.getItem(this.SESSION_ID_KEY);

    if (!sessionId) {
      sessionId = this.generateUUID();
      localStorage.setItem(this.SESSION_ID_KEY, sessionId);
      console.log('[Analytics] New session started:', sessionId);
    }

    return sessionId;
  }

  /**
   * Réinitialise le funnel_id (après création de course)
   * Cela permet de démarrer un nouveau funnel pour le prochain parcours
   */
  resetFunnel() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const oldFunnelId = localStorage.getItem(this.FUNNEL_ID_KEY);
    localStorage.removeItem(this.FUNNEL_ID_KEY);
    console.log('[Analytics] Funnel reset. Old funnel_id:', oldFunnelId);
  }

  /**
   * Réinitialise la session (déconnexion, etc.)
   */
  resetSession() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    localStorage.removeItem(this.SESSION_ID_KEY);
    localStorage.removeItem(this.FUNNEL_ID_KEY);
    console.log('[Analytics] Session reset');
  }

  /**
   * Génère un UUID v4
   * @returns {string} Un UUID v4 unique
   */
  generateUUID() {
    // Utiliser crypto.randomUUID si disponible (navigateurs modernes)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback pour navigateurs anciens
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (
      c,
    ) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Retourne les headers à inclure dans chaque requête API
   * @returns {Object} Headers contenant X-Funnel-Id et X-Session-Id
   */
  getHeaders() {
    return {
      'X-Funnel-Id': this.getFunnelId(),
      'X-Session-Id': this.getSessionId(),
    };
  }

  /**
   * Debug: affiche les IDs actuels dans la console
   */
  debug() {
    console.log('[Analytics Debug]', {
      funnel_id: this.getFunnelId(),
      session_id: this.getSessionId(),
      localStorage_funnel:
        typeof localStorage !== 'undefined'
          ? localStorage.getItem(this.FUNNEL_ID_KEY)
          : 'N/A',
      localStorage_session:
        typeof localStorage !== 'undefined'
          ? localStorage.getItem(this.SESSION_ID_KEY)
          : 'N/A',
    });
  }
}

// Export une instance singleton
export default new AnalyticsService();
